import type { FC, SVGProps } from "react"
import {
	IconAlignLeftOutline18 as AlignLeft,
	IconBalanceOutline18 as Balance,
	IconCircleCheckOutline18 as CircleCheck,
	IconLanguageOutline18 as Language,
	IconMessageArrowRightOutline18 as MessageArrowRight,
	IconMessagePlusOutline18 as MessagePlus,
	IconTagOutline18 as Tag,
	IconUserCheckOutline18 as UserCheck,
	IconUsersOutline18 as Users,
} from "nucleo-ui-outline-18"

import { ROUTINE_DEFAULT_PROMPT } from "@productlane/lib/src/ai-chat-defaults"
import { LinearIcon } from "@productlane/ui"

/** The footer row: what starts the routine, then what it does. */
export interface RoutineFooterStep {
	Icon: FC<SVGProps<SVGSVGElement>>
	label: string
}

export interface RoutineCard {
	id: string
	Icon: FC<SVGProps<SVGSVGElement>>
	/** Keeps each card apart at a glance; the rest of the card stays monochrome. */
	iconClassName: string
	title: string
	description: string
	trigger: RoutineFooterStep
	action: RoutineFooterStep
	/** Appended to the prompt verbatim, so the card shows exactly what lands. */
	line: string
}

/** The pitch cards and the add-a-line cards are the same shape. */
export type RoutineExample = RoutineCard
export type RoutineUseCase = RoutineCard

const NEW_THREAD: RoutineFooterStep = { Icon: MessagePlus, label: "New thread" }
const CUSTOMER_REPLY: RoutineFooterStep = {
	Icon: MessageArrowRight,
	label: "Customer reply",
}

export { ROUTINE_DEFAULT_PROMPT }

export const ROUTINE_BALANCE_LINE =
	"Balance new threads, max 25 open per person."

export const ROUTINE_IMPORTANCE_LINE =
	"Set importance to Important when a customer is blocked."

export const ROUTINE_EXAMPLES: RoutineExample[] = [
	{
		id: "auto-tag",
		Icon: Tag,
		iconClassName: "text-blue-500",
		title: "Auto tag",
		description:
			"Reuses the tags you already have, and creates one when none fit.",
		trigger: NEW_THREAD,
		action: { Icon: Tag, label: "Tag by request type" },
		line: ROUTINE_DEFAULT_PROMPT,
	},
	{
		id: "translate",
		Icon: Language,
		iconClassName: "text-purple-500",
		title: "Translate to English",
		description: "The translation arrives as a note only your team reads.",
		trigger: NEW_THREAD,
		action: { Icon: Language, label: "Post an English translation" },
		line: "Translate each message into English as a note.",
	},
	{
		id: "route-language",
		Icon: Users,
		iconClassName: "text-orange-500",
		title: "Route by language",
		description:
			"Name a language and a teammate, then those threads go to them.",
		trigger: NEW_THREAD,
		action: { Icon: UserCheck, label: "Assign a named teammate" },
		line: "Assign [language] threads to [teammate].",
	},
	{
		id: "balance-workload",
		Icon: Balance,
		iconClassName: "text-green-500",
		title: "Balance the workload",
		description:
			"Spreads new threads evenly across the team, up to 25 open per person.",
		trigger: NEW_THREAD,
		action: { Icon: UserCheck, label: "Assign the least busy teammate" },
		line: ROUTINE_BALANCE_LINE,
	},
	{
		id: "linear-issue",
		Icon: LinearIcon,
		iconClassName: "text-blue-500",
		title: "File a Linear issue",
		description:
			"A reproducible bug becomes an issue, linked back to the thread.",
		trigger: NEW_THREAD,
		action: { Icon: LinearIcon, label: "File a Linear issue" },
		line: "File a Linear issue for a reproducible bug.",
	},
	{
		id: "close-thanks",
		Icon: CircleCheck,
		iconClassName: "text-green-500",
		title: "Close a thanks reply",
		description: "A reply that is only thanks needs nobody to open it.",
		trigger: CUSTOMER_REPLY,
		action: { Icon: CircleCheck, label: "Mark the thread done" },
		line: "Mark the thread done when a reply is only thanks.",
	},
	{
		id: "summarize",
		Icon: AlignLeft,
		iconClassName: "text-orange-500",
		title: "Summarize long threads",
		description: "A long thread arrives with three lines of summary on it.",
		trigger: NEW_THREAD,
		action: { Icon: AlignLeft, label: "Post a summary" },
		line: "Summarize threads over 300 words in a note.",
	},
]

/** Case- and whitespace-insensitive, so a reformatted prompt still matches. */
export function promptContainsLine(prompt: string, line: string): boolean {
	const normalize = (value: string) =>
		value.toLowerCase().replace(/\s+/g, " ").trim()
	return normalize(prompt).includes(normalize(line))
}

export function appendLine(prompt: string, line: string): string {
	if (promptContainsLine(prompt, line)) return prompt
	const trimmed = prompt.trimEnd()
	return trimmed.length > 0 ? `${trimmed}\n${line}` : line
}

/** The empty state's pitch: what a routine does before anyone writes a word. */
export const ROUTINE_USE_CASES: RoutineUseCase[] = [
	{
		id: "sorted-on-arrival",
		Icon: Tag,
		iconClassName: "text-blue-500",
		title: "Sort the inbox",
		description: "Each thread arrives tagged, with its importance already set.",
		trigger: NEW_THREAD,
		action: { Icon: Tag, label: "Tag and set importance" },
		line: `${ROUTINE_DEFAULT_PROMPT}\n${ROUTINE_IMPORTANCE_LINE}`,
	},
	{
		id: "read-in-english",
		Icon: Language,
		iconClassName: "text-purple-500",
		title: "Translate to English",
		description:
			"Any language arrives with an English translation in the thread.",
		trigger: NEW_THREAD,
		action: { Icon: Language, label: "Post an English translation" },
		line: ROUTINE_EXAMPLES.find((e) => e.id === "translate")?.line ?? "",
	},
	{
		id: "right-person",
		Icon: Users,
		iconClassName: "text-orange-500",
		title: "Route by language",
		description:
			"Name a language and a teammate, then those threads go to them.",
		trigger: NEW_THREAD,
		action: { Icon: UserCheck, label: "Assign a named teammate" },
		line: ROUTINE_EXAMPLES.find((e) => e.id === "route-language")?.line ?? "",
	},
	{
		id: "balance-workload",
		Icon: Balance,
		iconClassName: "text-green-500",
		title: "Balance the workload",
		description:
			"Spreads new threads evenly across the team, up to 25 open per person.",
		trigger: NEW_THREAD,
		action: { Icon: UserCheck, label: "Assign the least busy teammate" },
		line: ROUTINE_BALANCE_LINE,
	},
]

/** Added examples leave the row, so it only ever offers what is missing. */
export function remainingExamples(prompt: string): RoutineExample[] {
	return ROUTINE_EXAMPLES.filter(
		(example) => !promptContainsLine(prompt, example.line),
	)
}

/** A card whose instruction is already in the prompt has nothing left to add. */
export function remainingUseCases(prompt: string): RoutineUseCase[] {
	return ROUTINE_USE_CASES.filter(
		(useCase) => !promptContainsLine(prompt, useCase.line),
	)
}
